import type { FunctionComponent } from 'react';
import { useState, Fragment } from 'react';
import { Inline, spacingHelper, mediaHelper } from '@property.com.au/ui-kit';
import styled from 'styled-components';
import { WebLink } from '../../../WebLink/WebLink';
import { trackAccordionInteraction } from '../../../../../analytics/event-tracking/trackAccordionInteraction';
import {
  DesktopSchoolFactsSubtitle,
  DesktopSchoolFactsSubtitleWrapper,
  LinksWrapper,
  SchoolFactsSubtitle,
  SchoolFactsTable,
  SchoolFactsTitle,
  SchoolName,
  StyledDivider,
  StyledSchoolAccordionItem,
  TitleWrapper,
  ZonedBadge,
  ZonedBadgePlaceholder,
} from './SchoolAccordionItem.styled';
import type { School } from '../../types';

const StyledWebLink = styled(WebLink)`
  /* NOTE: custom styling for desktop as cannot change sizeVariant prop dynamically without affecting SSR */
  ${mediaHelper('desktopMin')} {
    padding: ${spacingHelper('extraSmall large')};
  }
`;

export const SchoolAccordionItem: FunctionComponent<School> = ({
  schoolName,
  isDefaultOpen,
  isZoned,
  schoolFacts,
  webLinks,
  tracking,
}) => {
  const [expanded, setExpanded] = useState(isDefaultOpen);

  return (
    <StyledSchoolAccordionItem
      id={`school-accordion-item-${schoolName.replace(/\s/g, '-')}`}
      key={schoolName}
      title={
        <TitleWrapper>
          <SchoolName>{schoolName}</SchoolName>
          <DesktopSchoolFactsSubtitleWrapper>
            {schoolFacts.map(({ title, subtitle }) => (
              <DesktopSchoolFactsSubtitle key={title + subtitle} $isZoned={isZoned ?? false}>
                {subtitle}
              </DesktopSchoolFactsSubtitle>
            ))}
          </DesktopSchoolFactsSubtitleWrapper>
          {isZoned ? <ZonedBadge>ZONED</ZonedBadge> : <ZonedBadgePlaceholder />}
        </TitleWrapper>
      }
      headingLevel={0} // Removes automatic wrapping of title prop as semantic heading
      defaultExpanded={isDefaultOpen}
      $isZoned={isZoned ?? false}
      onToggle={(isNowExpanded) => {
        setExpanded(isNowExpanded ?? false);
        trackAccordionInteraction(isNowExpanded ? tracking.expand : tracking.collapse);
      }}
      dangerouslySetClassNames={{
        header: 'header',
        headerButton: 'headerButton',
        contentWrapper: 'contentWrapper',
        content: 'content',
        textTitle: 'textTitle',
        chevronWrapper: 'chevronWrapper',
      }}
    >
      <SchoolFactsTable>
        {schoolFacts.map(({ title, subtitle }, index) => (
          <Fragment key={title}>
            {index !== 0 && <StyledDivider />}
            <Inline justifyContent="center" gap="0px" inset="0px" grow>
              <SchoolFactsTitle $isZoned={isZoned ?? false}>{title}</SchoolFactsTitle>
              <SchoolFactsSubtitle $isZoned={isZoned ?? false}>{subtitle}</SchoolFactsSubtitle>
            </Inline>
          </Fragment>
        ))}
      </SchoolFactsTable>
      {webLinks && (
        <LinksWrapper>
          {webLinks.map((link) => (
            <StyledWebLink
              {...link}
              key={link.url}
              sizeVariant="small"
              {...(expanded ? {} : { tabIndex: -1 })}
              // This removes the web links from the tab order when the accordion item is not expanded aka. when the buttons are hidden.
            />
          ))}
        </LinksWrapper>
      )}
    </StyledSchoolAccordionItem>
  );
};
